import type { BuroValidatorRequestPF, BuroValidatorRequestPFAE } from "@api/sat_apis_new/satApsi";
import { STATUS_CATALOG } from "@helpers/constantes";
import { truncateText } from "@helpers/funciones";
import { CompanyData, LoanApplication } from "@interfaces/store";
import { AddressData, PersonaStructure } from "@interfaces/response";
import { NipBuroPhone, NipBuroVariant } from "@interfaces/nipBuro";

/** Buró acepta hasta 26 caracteres en nombres y colonia, y 20 en los números del domicilio. */
const BURO_NAME_MAX = 26;
const BURO_NUMBER_MAX = 20;

interface NipBuroConfig {
  screenStatus: string;
  checkSatBureau: boolean;
  /** Textos legales del tax system (`TextosLegales`): la empresa (PFAE) o la persona física (PF, Trans Union). */
  textos: "buro_empresa" | "buro_persona";
  subtitle: string;
  /** Texto de la tarjeta "Autorización registrada". */
  autorizado: string;
}

/** Lo que cambia entre variantes y no es un servicio. Al migrar otra variante se agrega aquí. */
export const NIP_BURO_CONFIG: Record<NipBuroVariant, NipBuroConfig> = {
  pfae: {
    screenStatus: STATUS_CATALOG.consulta_buro_pfae.code,
    checkSatBureau: true,
    textos: "buro_empresa",
    subtitle:
      "Con un solo NIP autorizas dos consultas: como persona física en Trans Union y como actividad empresarial en Dun & Bradstreet. Enviamos un código de 4 dígitos por SMS y WhatsApp.",
    autorizado:
      "Quedan autorizadas las dos consultas: Trans Union de México (persona física) y Dun & Bradstreet (actividad empresarial).",
  },
  // Legacy `ConsultaBuroNipPF` + `ModalBuroNipPF`: la persona física titular (ej. WuWei).
  pf: {
    screenStatus: STATUS_CATALOG.consulta_buro_pf.code,
    checkSatBureau: true,
    textos: "buro_persona",
    subtitle:
      "Para continuar autoriza la consulta de tu historial en Buró de Crédito. Enviamos un código de 4 dígitos por SMS y WhatsApp.",
    autorizado: "Quedó autorizada la consulta de tu historial en Trans Union de México.",
  },
};

/**
 * Teléfono al que se manda el NIP: PFAE usa el del titular guardado en `company`; PF, el de la persona titular
 * (`legal_representative`), como el legacy.
 */
export const getNipBuroPhone = (
  variant: NipBuroVariant,
  company: CompanyData,
  person: PersonaStructure | undefined,
): NipBuroPhone =>
  variant === "pf"
    ? { countryCode: person?.code_phone_number ?? "", number: person?.phone_number ?? "" }
    : {
        countryCode: company.country_code_legal_representative ?? "",
        number: company.phone_legal_representative ?? "",
      };

/**
 * Consulta del titular PFAE (legacy `ModalBuroNipPfae`): sin domicilio y con el nombre comercial como
 * `legal_name`. El legacy la manda como `person_Type: "pf"` y sin `application_code`; se copia igual.
 */
export const buildBuroPayloadPfae = (
  company: CompanyData,
  application: LoanApplication,
  personId: string,
): BuroValidatorRequestPFAE => ({
  person_Type: "pf",
  data: {
    rfc: company.rfc,
    transaction_number: String(application.id),
    legal_name: truncateText(company.name || "", BURO_NAME_MAX),
    first_name: truncateText(company.pfae_name || "", BURO_NAME_MAX),
    second_name: "",
    father_last_name: truncateText(company.pfae_last_name || "", BURO_NAME_MAX),
    mother_last_name: truncateText(company.pfae_last_name_2 || "", BURO_NAME_MAX),
    reenter_NIP: true,
    authorization_label_response: true,
    loan_application_id: String(application.id),
    is_endorsement: false,
    person_id: personId,
  },
});

/**
 * Consulta de la persona física titular (legacy `ModalBuroNipPF`): nombre, RFC y CURP de la persona y su domicilio
 * particular. `colonyName` es el nombre de la colonia del catálogo (o la "otra colonia" capturada).
 */
export const buildBuroPayloadPf = (
  person: PersonaStructure,
  application: LoanApplication,
  domicilio: AddressData,
  colonyName: string,
): BuroValidatorRequestPF => ({
  person_Type: "pf",
  data: {
    rfc: person.rfc ?? "",
    curp: person.curp ?? "",
    transaction_number: String(application.id),
    application_code: application.company_application_code ?? "",
    legal_name: truncateText(person.name ?? "", BURO_NAME_MAX),
    first_name: truncateText(person.name ?? "", BURO_NAME_MAX),
    second_name: "",
    father_last_name: truncateText(person.last_name ?? "", BURO_NAME_MAX),
    mother_last_name: truncateText(person.last_name_2 ?? "", BURO_NAME_MAX),
    reenter_NIP: true,
    authorization_label_response: true,
    loan_application_id: String(application.id),
    is_endorsement: false,
    person_id: String(person.id),
    address: {
      street: domicilio.street,
      exteriorNumber: truncateText(domicilio.no_ext || "", BURO_NUMBER_MAX),
      interiorNumber: truncateText(domicilio.no_int || "", BURO_NUMBER_MAX),
      state: domicilio.state?.name ?? "",
      municipality: domicilio.locality?.name ?? "",
      locality: truncateText(colonyName, BURO_NAME_MAX),
      postalCode: domicilio.cp,
    },
  },
});
